import { LoaderTalos } from '@talos/kyoko';
import lazyWithPrefetch from '@talos/kyoko/src/utils/lazyWithPrefetch';
import type { GroupviewPanelState } from 'dockview';
import { Suspense } from 'react';
import type { LayoutSpec } from '../../../types/LayoutConfig';
import { MobileBalancesBlotter } from './Blotters/MobileBalancesBlotter';
import { MobileCreditBlotter } from './Blotters/MobileCreditBlotter';
import { MobileHedgePositionStatusBlotter } from './Blotters/MobileHedgePositionStatusBlotter';
import { MobileLoansBlotter } from './Blotters/MobileLoansBlotter';
import { MobileOrdersBlotter } from './Blotters/MobileOrdersBlotter';
import { MobileOTCDerivativesBlotter } from './Blotters/MobileOTCDerivativesBlotter';
import { MobilePositionsBlotter } from './Blotters/MobilePositionsBlotter';
import { MobileReconMismatchesBlotter } from './Blotters/MobileReconMismatchesBlotter';
import { MobileTradesBlotter } from './Blotters/MobileTradesBlotter';
import { MobileTransfersBlotter } from './Blotters/MobileTransfersBlotter';
import { MobileMarketCards } from './Markets/MobileMarketCards';
import { MobileMarketPanel } from './Markets/MobileMarketPanel';
import { TradingPlaceholder } from './TradingPlaceholder';
import { getPanelConfig } from './utils/getPanelConfig';

const MobileWatchlistPanel = lazyWithPrefetch(
  () => import('@src/containers/Mobile/Trading/Markets/MobileWatchlistPanel')
);

MobileWatchlistPanel.prefetch();

export interface MobileTradingPanelDispatcherProps {
  layout: LayoutSpec;
  panel: GroupviewPanelState;
}

/**
 * Maps a trading `panel.params.type` to its mobile component. Each `case` narrows `panelConfig` to the
 * matching discriminated-union member, so child components receive a specific `PanelConfig` variant
 * without additional casts. Unsupported panel types fall back to the shared placeholder.
 */
export function MobileTradingPanelDispatcher({ layout, panel }: MobileTradingPanelDispatcherProps) {
  const panelConfig = getPanelConfig(panel);

  const content = (() => {
    switch (panelConfig?.type) {
      case 'ordersBlotter':
        return <MobileOrdersBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'tradesBlotter':
        return <MobileTradesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'positionsBlotter':
        return <MobilePositionsBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'balancesBlotter':
        return <MobileBalancesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'creditBlotter':
        return <MobileCreditBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'otcDerivatives':
        return <MobileOTCDerivativesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'loans':
        return <MobileLoansBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'transfersBlotter':
        return <MobileTransfersBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'reconMismatches':
        return <MobileReconMismatchesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'hedgePositionStatus':
        return <MobileHedgePositionStatusBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'marketCards':
        return (
          <MobileMarketPanel panel={panel}>
            <MobileMarketCards panel={panel} panelConfig={panelConfig} />
          </MobileMarketPanel>
        );
      case 'watchlist':
        return (
          <MobileMarketPanel panel={panel}>
            <MobileWatchlistPanel panel={panel} panelConfig={panelConfig} />
          </MobileMarketPanel>
        );
      default:
        return <TradingPlaceholder label={panel.title ?? ''} />;
    }
  })();

  return <Suspense fallback={<LoaderTalos />}>{content}</Suspense>;
}

export default MobileTradingPanelDispatcher;
